import type { IssueDefinition } from "../../issue-definition";
import { ISSUE_CATEGORY } from "../../issue-enums";

/**
 * The shared platform-dr vault's converge, watched for the failure it can
 * silently repeat.
 *
 * `EnsurePlatformDrStacksUseCase` converges one `platform-dr` stack per DR
 * destination region on every reconcile tick. It never throws and never
 * returns an error BY DESIGN — a stuck region must not take the tenant sweep
 * that hosts it down — so a per-tick failure was, until this code existed,
 * logged and counted and then thrown away one line later.
 *
 * 🔴 IT EXISTS BECAUSE THE COUNT ALREADY EXISTED AND NOTHING WAS ALLOWED TO
 * ACT ON IT. During NEX-2915, three platform-dr stacks failed roughly three
 * times a day, for days, and nobody saw it: a region that fails every single
 * window was indistinguishable from one that succeeds every window, because
 * both are silent. The information was in the cron execution log the entire
 * time.
 *
 * PER REGION, NOT PER TENANT. The vault belongs to no organization — it is
 * fleet infrastructure that every tenant whose backups copy INTO that region
 * depends on. The Issue ledger has no region-scoped entity (same gap
 * `VIBE_FORGEJO_VOLUME_ENCRYPTION_UNOBSERVABLE` and
 * `VIBE_ZONE_TOTAL_TLS_DISABLED` both name), so this is raised against every
 * currently-serving tenant whose DR destination is the failing region —
 * inventing a region-scoped entity for one row would still be the wrong
 * shape, and the tenants whose DR copy is at risk ARE those clusters.
 *
 * CONSECUTIVE, NOT CUMULATIVE. A region that failed a handful of scattered
 * ticks over months is not a stuck stack; three failures in a row at a
 * ~2-minute cadence is. The counter resets on every successful converge, so
 * only a genuinely stuck run reaches the threshold — see
 * `platformDrConvergeFailureKey`.
 *
 * OUT OF SCOPE, DELIBERATELY: this does not repair anything. The orphaning
 * mechanism that makes a region get stuck is its own problem (NEX-2928). This
 * code's whole job is making a stuck region visible instead of silent.
 */
export const VIBE_PLATFORM_DR_CONVERGE_FAILING = {
  category: ISSUE_CATEGORY.OBSERVABILITY,
  message:
    "The shared platform-dr vault for this DR destination region has failed to converge for several consecutive reconcile ticks. This vault is fleet infrastructure — every tenant whose cross-region backup copy lands here depends on it existing and being current — so a stuck converge here is a silent risk to every one of those tenants' DR guarantees, not a fact about any single tenant's own infrastructure. The consecutive-failure count and the last failure reason are on the issue payload. Cleared automatically as soon as one ensure tick converges this region again.",
  severity: "high",
  remedy:
    "Check the cron execution log for the platform-dr ensure tick (VIBE_TENANT_CLUSTER_RECONCILE_TICK) and read the failure reason recorded on the issue payload — it names why the converge failed (a Pulumi state fault, a provisioner error, an IAM/credentials problem on the identity that drives this stack). If the reason points at a transient external fault, the next window retries on its own with no action needed. If the count keeps climbing and the reason repeats, the shared vault's Pulumi state is genuinely stuck: select the tenant-<region>-platform-dr stack directly (see the standard Pulumi identity setup — human admin profile, not a Vibe principal, since the control plane cannot reach this state) and run a manual `pulumi up` to see the actual apply-time error instead of the ensure tick's summarized one. This alarm names the tenants whose DR copies are affected; it does not name a lever to fix the vault itself, because the fix is stack-specific."
} as const satisfies IssueDefinition;
